import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import type { CustomCharge } from '../types/report.types';

// Component props
interface CustomChargesReportTableProps {
  data: CustomCharge[];
  columns?: ColumnDef<CustomCharge, unknown>[];
  isLoading?: boolean;
}

export const CustomChargesReportTable: React.FC<
  CustomChargesReportTableProps
> = ({ data, columns = customChargesColumns, isLoading = false }) => {
  const table = useReactTable<CustomCharge>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading)
    return (
      <div className="p-4 space-y-2">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="h-10 bg-gray-300 animate-pulse rounded" />
        ))}
      </div>
    );

  return (
    <div
      className="min-w-3xl min-h-screen"
      style={{ scrollbarWidth: 'none' }}
    >
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className="p-2 md:p-4 text-sm md:text-base text-center border">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="p-2 md:p-4 text-sm md:text-base text-start border">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="py-6 text-start text-gray-400"
              >
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// Columns definition for Custom Charges
const customChargesColumns: ColumnDef<CustomCharge>[] = [
  {
    id: 'resident',
    header: () => <div className="font-bold">Resident</div>,
    cell: (info) => {
      const row = info.row.original;
      return (
        <div className="">
          <p>{row.residentName ?? '-'}</p>
          <p className="text-gray-500 text-sm">{row.houseName ?? '-'}</p>
        </div>
      );
    },
  },
  {
    accessorKey: 'type',
    header: () => <div className="font-bold">Type</div>,
    cell: (info) => <div className="">{String(info.getValue() ?? '-')}</div>,
  },
  {
    accessorKey: 'description',
    header: () => <div className="font-bold">Description</div>,
    cell: (info) => <div className="">{String(info.getValue() ?? '-')}</div>,
  },
  {
    accessorKey: 'date',
    header: () => <div className="text-center font-bold">Date</div>,
    cell: (info) => {
      const date = info.getValue() as string;
      return (
        <div className="text-center">
          {new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
          })}
        </div>
      );
    },
  },

  {
    accessorKey: 'amount',
    header: () => <div className="text-center font-bold">Amount</div>,
    cell: (info) => (
      <div className="text-center text-green-700">
        ${String(info.getValue() ?? 0)}
      </div>
    ),
  },
];
